import React from "react";
import Divider from '@mui/material/Divider';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { motion } from "framer-motion";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";

import { Iphone } from "@assets";
import { useDarkMode } from "@hooks/dark-mode";

// Animation variants
const animations = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  },
  item: {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 150, duration: 0.3 }
    }
  },
  chart: {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 150, delay: 0.2 }
    }
  },
  feature: {
    hidden: { opacity: 0, x: -20 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.1 + (i * 0.05),
        duration: 0.3
      }
    })
  },
  phone: {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { type: "spring", stiffness: 100, delay: 0.1, duration: 0.3 },
    hover: { y: -10, transition: { duration: 0.2 } }
  }
};

// Feature list component
const FeatureList = ({ features }: { features: { title: string; text: string }[] }) => {
  const { dark: isDarkTheme } = useDarkMode();
  const featureColor = isDarkTheme ? '#80ABF9' : '#1673FF';
  
  return (
    <div className="grid gap-4 mt-4">
      {features.map((feature, i) => (
        <motion.div
          key={i}
          className="flex items-start justify-center gap-2"
          custom={i}
          variants={animations.feature}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <CheckCircleOutlineIcon sx={{ color: featureColor }} />
          <p className="font-light text-gray-700 md:text-lg dark:text-gray-300">
            <strong style={{ color: featureColor }}>{feature.title}</strong> {feature.text}
          </p>
        </motion.div>
      ))}
    </div>
  );
};

// Section component
const InfoSection = ({
  title,
  description,
  features,
  className = ""
}: {
  title: string;
  description: string;
  features: { title: string; text: string }[];
  className?: string;
}) => {
  const { dark: isDarkTheme } = useDarkMode();

  return (
    <motion.div
      className={`mt-4 items-center justify-center ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={animations.container}
    >
      <motion.h2
        className="mb-4 text-4xl tracking-tight font-extrabold text-black"
        variants={animations.item}
      >
        {title}
      </motion.h2>
      <motion.p
        className="mb-6 font-light text-gray-700 md:text-lg dark:text-gray-300"
        variants={animations.item}
      >
        {description}
      </motion.p>
      <Divider sx={{ bgcolor: isDarkTheme ? 'rgba(255, 255, 255, 0.7)' : undefined }} />
      <FeatureList features={features} />
    </motion.div>
  );
};

export default function Overview() {
  const { t } = useTranslation();
  const { dark: isDarkTheme } = useDarkMode();

  const appFeatures = [
    {
      title: t('home.overviewSection.app.features.registration.title'),
      text: t('home.overviewSection.app.features.registration.text')
    },
    {
      title: t('home.overviewSection.app.features.noEngage.title'),
      text: t('home.overviewSection.app.features.noEngage.text')
    },
    {
      title: t('home.overviewSection.app.features.realTime.title'),
      text: t('home.overviewSection.app.features.realTime.text')
    },
    {
      title: t('home.overviewSection.app.features.greenGoals.title'),
      text: t('home.overviewSection.app.features.greenGoals.text')
    }
  ];

  const dashboardFeatures = [
    {
      title: t('home.overviewSection.dashboard.features.aggregated.title'),
      text: t('home.overviewSection.dashboard.features.aggregated.text')
    },
    {
      title: t('home.overviewSection.dashboard.features.compliance.title'),
      text: t('home.overviewSection.dashboard.features.compliance.text')
    },
    {
      title: t('home.overviewSection.dashboard.features.insights.title'),
      text: t('home.overviewSection.dashboard.features.insights.text')
    },
    {
      title: t('home.overviewSection.dashboard.features.engage.title'),
      text: t('home.overviewSection.dashboard.features.engage.text')
    }
  ];

  const emissionsData = [45, 52, 38, 24, 33, 40];
  const totalEmissions = emissionsData.reduce((acc, curr) => acc + curr, 0);
  const commutesData = [15, 20, 12, 6, 10, 12];
  const distanceData = [285.42, 87.23, 63.77, 230.05, 62.53];

  const translatedMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"].map(month => t(`home.overviewSection.charts.months.${month.toLowerCase()}` as const));
  const translatedTransportMethods = ["Car", "Bus", "Train", "Bike", "Walking"].map(method => t(`home.overviewSection.charts.transport.methods.${method.toLowerCase()}` as const));

  const chartBgColor = isDarkTheme ? "#28282B" : "#FFFFFF";

  return (
    <section
      id="overview"
      className="bg-secBg flex justify-center items-center"
      style={{ padding: '4rem 0' }}>

      <div className="md:grid-rows-2">
        {/* App Section */}
        <div className="gap-8 items-center px-4 mx-auto max-w-screen-xl md:grid md:grid-cols-2 sm:py-16 lg:px-20">
          <InfoSection
            title={t('home.overviewSection.app.title')}
            description={t('home.overviewSection.app.description')}
            features={appFeatures}
          />

          <motion.div
            className="flex justify-center py-6"
            initial={animations.phone.initial}
            whileInView={animations.phone.animate}
            transition={animations.phone.transition}
            viewport={{ once: true }}
          >
            <motion.img
              src={Iphone}
              alt="iphone"
              className="w-1/2 max-w-xs items-center justify-center"
              whileHover={animations.phone.hover}
            />
          </motion.div>
        </div>

        {/* Dashboard Section */}
        <div className="px-4 max-w-screen-xl md:grid md:grid-cols-2 sm:py-16 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={animations.container}
          >
            {/* Emissions Chart */}
            <motion.div
              style={{ backgroundColor: chartBgColor }}
              className="rounded-lg p-4 shadow sm:p-6"
              variants={animations.chart}
              whileHover={{ scale: 1.02, transition: { duration: 0.15 } }}
            >
              <h3 className="mb-2 text-2xl tracking-tight font-bold text-black">
                {t('home.overviewSection.charts.emissions.title', { totalEmissions })}
              </h3>
              <p className="font-light text-gray-700 md:text-lg dark:text-gray-300">
                {t('home.overviewSection.charts.emissions.subtitle')}
              </p>
              <Chart
                options={{
                  chart: {
                    type: "area",
                    fontFamily: "Inter, sans-serif",
                    foreColor: "#6B7280",
                    toolbar: { show: false },
                    zoom: { enabled: false },
                    animations: {
                      enabled: true,
                      easing: 'easeinout',
                      speed: 500,
                      animateGradually: { enabled: true, delay: 100 },
                      dynamicAnimation: { enabled: true, speed: 250 }
                    }
                  },
                  stroke: { curve: "smooth", width: 2 },
                  fill: {
                    type: "gradient",
                    gradient: {
                      opacityFrom: 0.45,
                      opacityTo: 0,
                      type: "vertical"
                    }
                  },
                  dataLabels: { enabled: false },
                  colors: ["#1673FF"],
                  xaxis: {
                    categories: translatedMonths,
                    labels: { style: { fontSize: "12px", fontWeight: 500 } }
                  },
                  yaxis: {
                    labels: { formatter: (value: number) => t('home.overviewSection.charts.emissions.yAxisLabel', { value: value.toFixed(0) }) }
                  },
                  grid: {
                    show: true,
                    borderColor: "#F3F4F6",
                    strokeDashArray: 1
                  },
                  markers: { size: 5 }
                }}
                series={[{ name: t('home.overviewSection.charts.emissions.seriesName'), data: emissionsData }]}
                type="area"
                width="100%"
                height="70%"
              />
            </motion.div>

            <div className="mt-8 grid grid-cols-2 gap-6">
              {/* Commutes Chart */}
              <motion.div
                className="rounded-lg bg-secBg p-4 shadow sm:p-6"
                style={{ backgroundColor: chartBgColor }}
                variants={animations.chart}
                whileHover={{ scale: 1.02, transition: { duration: 0.15 } }}
              >
                <div className="flex items-center">
                  <div className="shrink-0">
                    <motion.span
                      className="text-2xl font-bold leading-none text-black sm:text-3xl"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.3 }}
                      viewport={{ once: true }}
                    >
                      42
                    </motion.span>
                    <h3 className="text-base font-normal text-gray-600 dark:text-gray-400">
                      {t('home.overviewSection.charts.commutes.subtitle')}
                    </h3>
                  </div>
                </div>
                <Chart
                  options={{
                    labels: translatedMonths,
                    chart: {
                      fontFamily: "Inter, sans-serif",
                      toolbar: { show: false },
                      foreColor: isDarkTheme ? "#A1A5B2" : "#4B5563",
                      animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 500,
                        dynamicAnimation: { enabled: true, speed: 250 }
                      }
                    },
                    theme: {
                      monochrome: { enabled: true, color: "#1673FF" }
                    },
                    plotOptions: {
                      bar: {
                        columnWidth: "25%",
                        borderRadius: 3,
                        colors: {
                          backgroundBarColors: Array(6).fill("#E5E7EB"),
                          backgroundBarRadius: 3,
                        },
                      },
                    },
                    xaxis: {
                      floating: true,
                      labels: { show: false },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                    },
                    tooltip: {
                      hideEmptySeries: false,
                      shared: true,
                      intersect: false,
                      style: { fontSize: "14px", fontFamily: "Inter, sans-serif" },
                    },
                    states: {
                      hover: { filter: { type: "darken", value: 0.8 } },
                    },
                    fill: { opacity: 1 },
                    yaxis: { show: false },
                    grid: { show: false },
                    dataLabels: { enabled: false },
                    legend: { show: true },
                  }}
                  series={[{ name: t('home.overviewSection.charts.commutes.seriesName'), data: commutesData }]}
                  type="bar"
                  height={220}
                />
              </motion.div>

              {/* Transportation Method Chart */}
              <motion.div
                className="rounded-lg bg-secBg p-4 shadow sm:p-6"
                style={{ backgroundColor: chartBgColor }}
                variants={animations.chart}
                whileHover={{ scale: 1.02, transition: { duration: 0.15 } }}
              >
                <motion.span
                  className="text-2xl font-bold leading-none text-black sm:text-3xl"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                  viewport={{ once: true }}
                >
                  {t('home.overviewSection.charts.transport.mostUtilizedValue')}
                </motion.span>
                <p className="text-base font-normal text-gray-600 dark:text-gray-400">
                  {t('home.overviewSection.charts.transport.mostUtilized')}
                </p>
                <Chart
                  options={{
                    chart: {
                      type: "pie",
                      fontFamily: "Inter, sans-serif",
                      animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 500,
                        animateGradually: { enabled: true, delay: 100 },
                        dynamicAnimation: { enabled: true, speed: 250 }
                      }
                    },
                    colors: ["#16BDCA", "#FDBA8C", "#1A56DB", "#D61F69", "#9061F9"],
                    stroke: {
                      colors: [isDarkTheme ? "#111827" : "#fff"],
                    },
                    labels: translatedTransportMethods,
                    plotOptions: {
                      pie: { donut: { size: "100%" } },
                    },
                    dataLabels: { enabled: false },
                    tooltip: {
                      x: {
                        show: true,
                        formatter: (val, { dataPointIndex, w }) =>
                          w.config.labels[dataPointIndex],
                      },
                      y: {
                        formatter: (value: number) => t('home.overviewSection.charts.transport.tooltipFormat', { value: value.toFixed(2) }),
                      },
                      theme: 'dark',
                      style: {
                        fontSize: '12px',
                        fontFamily: 'Inter, sans-serif'
                      },
                      fillSeriesColor: false,
                      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                        const label = w.config.labels[seriesIndex];
                        const value = series[seriesIndex];
                        return `<div class="apexcharts-tooltip-box" style="background-color: #4B5563; color: white; padding: 6px 8px; border-radius: 4px;">
                          <span>${label}: ${value.toFixed(2)} km</span>
                        </div>`;
                      }
                    },
                    legend: { show: false }
                  }}
                  series={distanceData}
                  type="pie"
                />
              </motion.div>
            </div>
          </motion.div>

          <InfoSection
            title={t('home.overviewSection.dashboard.title')}
            description={t('home.overviewSection.dashboard.description')}
            features={dashboardFeatures}
            className="ml-6"
          />
        </div>
      </div>
    </section>
  );
}
